"use client";

import {
	GEO_PREF_COOKIE,
	parseGeoPrefCookieValue,
	dispatchGeoPrefUpdated,
	normalizeReferenceFxCurrencyCode,
} from "@/lib/geo/geo-pref";
import { getReferenceCurrencyForContext } from "@/lib/pricing/reference-currency-policy";

/** Read raw `geo_pref` value from `document.cookie` (browser only). */
export function readGeoPrefCookieRaw(): string | undefined {
	if (typeof document === "undefined") return undefined;
	const m = document.cookie.match(new RegExp(`(?:^|; )${GEO_PREF_COOKIE}=([^;]*)`));
	return m ? decodeURIComponent(m[1]) : undefined;
}

export type SetGeoPrefCookieParams = {
	locale: string;
	channel: string;
	/** Omit to keep existing `referenceCurrency` in the cookie. Pass `null` to clear. */
	referenceCurrency?: string | null;
};

/**
 * Writes `geo_pref` JSON (locale, channel, optional referenceCurrency).
 * Dispatches `bergdorf-geo-pref-updated` for client listeners (e.g. cart pricing).
 */
export function setGeoPrefCookie(params: SetGeoPrefCookieParams): void {
	const prev = parseGeoPrefCookieValue(readGeoPrefCookieRaw());
	let ref: string | undefined;
	if (params.referenceCurrency !== undefined) {
		const n = normalizeReferenceFxCurrencyCode(params.referenceCurrency);
		ref = n ?? undefined;
	} else {
		ref = prev?.referenceCurrency;
	}
	const payload = {
		locale: params.locale,
		channel: params.channel.trim().toLowerCase(),
		...(ref ? { referenceCurrency: ref } : {}),
	};
	document.cookie = `${GEO_PREF_COOKIE}=${encodeURIComponent(JSON.stringify(payload))}; path=/; max-age=${365 * 86400}; samesite=lax`;
	dispatchGeoPrefUpdated();
}

/**
 * Browser-only: effective reference display currency (cookie override or policy fallback).
 * Use for `useEffect` sync — **not** for `useState` initial value (use `getReferenceCurrencyForContext` for SSR-safe policy default).
 */
export function resolveReferenceCurrencyClient(
	locale: string,
	channel: string,
	countryCode: string | null = null,
): string {
	const cookie = parseGeoPrefCookieValue(readGeoPrefCookieRaw());
	if (cookie?.referenceCurrency) return cookie.referenceCurrency;
	return getReferenceCurrencyForContext({ countryCode, locale, channel });
}
